import React, { useState, useRef, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { useAtomValue } from 'jotai/utils';
import { chatStreamingAtom, chatFocusRequestAtom } from 'src/shared/chat';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { improvePrompt } from 'src/api/functions/chatApi';
import type { SuggestionChip } from './types';
import { useChatContext } from './useChatContext';
import { CreditRing } from './CreditRing';
import { AgentModeToggle } from './AgentModeToggle';
import styles from './ChatInput.module.scss';

interface ChatInputProps {
  onSend: (message: string, mcpPrompt?: string, mcpPromptArgs?: Record<string, string>) => void;
  onStop?: () => void;
  pendingChip?: SuggestionChip | null;
  onPendingChipConsumed?: () => void;
  disabled?: boolean;
}

// Feature flag: gate the entire Improve button so the feature can ship
// dark and be enabled per environment without a code change. Defaults
// to enabled when the flag is unset to keep dev experience friction-free;
// production builds set REACT_APP_IMPROVE_PROMPT_ENABLED=false until
// the proxy + SaaS-side changes are deployed.
const IMPROVE_PROMPT_ENABLED =
  process.env.REACT_APP_IMPROVE_PROMPT_ENABLED !== 'false';

// Auto-dismiss windows for the inline hints in the footer row. Long
// enough to read, short enough to fade before the user starts typing
// the next message.
const REVERT_HINT_TIMEOUT_MS = 10_000;
const NO_CHANGE_HINT_TIMEOUT_MS = 5_000;

type FooterHint =
  | { kind: 'revert'; previous: string }
  | { kind: 'no_change' }
  | null;

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, onStop, pendingChip, onPendingChipConsumed, disabled }) => {
  const [value, setValue] = useState('');
  const pendingPromptRef = useRef<{ mcpPrompt?: string; mcpArgs?: Record<string, string> } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isStreaming = useAtomValue(chatStreamingAtom);
  const focusRequest = useAtomValue(chatFocusRequestAtom);
  const accessToken = useAccessToken();
  const chatContext = useChatContext();
  const runMode = chatContext.agentMode === 'run';
  const { showAlert } = useAlerts();

  const [isImproving, setIsImproving] = useState(false);
  const [footerHint, setFooterHint] = useState<FooterHint>(null);
  const hintTimerRef = useRef<number | null>(null);

  const clearHintTimer = useCallback(() => {
    if (hintTimerRef.current !== null) {
      window.clearTimeout(hintTimerRef.current);
      hintTimerRef.current = null;
    }
  }, []);

  const scheduleHintDismiss = useCallback(
    (ms: number) => {
      clearHintTimer();
      hintTimerRef.current = window.setTimeout(() => {
        setFooterHint(null);
        hintTimerRef.current = null;
      }, ms);
    },
    [clearHintTimer],
  );

  useEffect(() => clearHintTimer, [clearHintTimer]);

  useEffect(() => {
    if (!pendingChip) return;
    // Starter chips send straight away, through the same onSend as a typed
    // message, so Plan/Run mode and the budget pre-flight apply unchanged. If
    // the composer can't send right now, fall through to a prefill so the
    // click isn't lost.
    if (pendingChip.sendNow && !isStreaming && !disabled) {
      const text = (pendingChip.message || pendingChip.label).trim();
      onPendingChipConsumed?.();
      if (text) onSend(text, pendingChip.mcpPrompt, pendingChip.mcpArgs);
      return;
    }
    setValue(pendingChip.message || pendingChip.label);
    pendingPromptRef.current = pendingChip.mcpPrompt
      ? { mcpPrompt: pendingChip.mcpPrompt, mcpArgs: pendingChip.mcpArgs }
      : null;
    setFooterHint(null);
    clearHintTimer();
    onPendingChipConsumed?.();
    requestAnimationFrame(() => {
      adjustHeight();
      textareaRef.current?.focus();
    });
  }, [pendingChip]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus-only request (no draft change) — see chatFocusRequestAtom.
  useEffect(() => {
    if (!focusRequest) return;
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [focusRequest]);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 5 * 24;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    pendingPromptRef.current = null;
    if (footerHint) {
      setFooterHint(null);
      clearHintTimer();
    }
    adjustHeight();
  };

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isStreaming || disabled) return;
    const pending = pendingPromptRef.current;
    onSend(trimmed, pending?.mcpPrompt, pending?.mcpArgs);
    setValue('');
    pendingPromptRef.current = null;
    setFooterHint(null);
    clearHintTimer();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, isStreaming, disabled, onSend, clearHintTimer]);

  const canImprove =
    IMPROVE_PROMPT_ENABLED && !!value.trim() && !isStreaming && !disabled && !isImproving;

  const handleImprove = useCallback(async () => {
    const draft = value.trim();
    if (!draft || isImproving || isStreaming || disabled) return;

    const previous = value;
    const pending = pendingPromptRef.current;
    setIsImproving(true);
    try {
      const res = await improvePrompt({
        draft,
        context: chatContext,
        mcpPrompt: pending?.mcpPrompt,
        mcpPromptArgs: pending?.mcpArgs,
        accessToken,
      });

      if (!res.ok) {
        // 429 carries Retry-After in a header we don't surface here
        // because we'd need to plumb the raw Response — keep the alert
        // text generic and human-friendly instead.
        const message =
          res.error.status === 429
            ? 'Slow down a bit — try again in a moment.'
            : res.error.status === 413
              ? 'That draft is too long to improve. Try shortening it first.'
              : 'Couldn\u2019t improve the prompt right now. Please try again.';
        showAlert(message);
        return;
      }

      if (res.data.no_change) {
        // Don't replace the textarea; the rewriter judged the draft
        // already clear. Surface a low-key hint instead.
        setFooterHint({ kind: 'no_change' });
        scheduleHintDismiss(NO_CHANGE_HINT_TIMEOUT_MS);
        return;
      }

      setValue(res.data.improved);
      // Preserve any catalogue-prompt binding so the rewritten draft
      // still triggers the MCP prompt path on send. The user explicitly
      // chose that prompt; the rewrite only specialises its phrasing.
      requestAnimationFrame(() => {
        adjustHeight();
        textareaRef.current?.focus();
      });
      setFooterHint({ kind: 'revert', previous });
      scheduleHintDismiss(REVERT_HINT_TIMEOUT_MS);
    } catch (err) {
      showAlert('Couldn\u2019t improve the prompt right now. Please try again.');
    } finally {
      setIsImproving(false);
    }
  }, [
    value,
    isImproving,
    isStreaming,
    disabled,
    chatContext,
    accessToken,
    showAlert,
    adjustHeight,
    scheduleHintDismiss,
  ]);

  const handleRevert = useCallback(() => {
    if (footerHint?.kind !== 'revert') return;
    setValue(footerHint.previous);
    setFooterHint(null);
    clearHintTimer();
    requestAnimationFrame(() => {
      adjustHeight();
      textareaRef.current?.focus();
    });
  }, [footerHint, clearHintTimer, adjustHeight]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    // Cmd/Ctrl+I rewrites the draft. Match the textarea-only scope so
    // global keybindings elsewhere on the page are not affected.
    if ((e.metaKey || e.ctrlKey) && (e.key === 'i' || e.key === 'I')) {
      if (canImprove) {
        e.preventDefault();
        void handleImprove();
      }
    }
  };

  return (
    <div className={styles.inputContainer}>
      <div className={cn(styles.inputWrapper, runMode && styles.inputWrapperRun)}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            disabled
              ? 'Usage limit reached'
              : runMode
                ? 'Ask AI to help with your assets — changes run right away'
                : 'Ask AI to help with your assets...'
          }
          disabled={isStreaming || disabled}
          rows={2}
          aria-label="Chat message input"
        />
        {isStreaming && onStop ? (
          <button
            className={cn(styles.sendBtn, styles.stopBtn)}
            onClick={onStop}
            title="Stop generating"
            aria-label="Stop generating"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
            </svg>
          </button>
        ) : (
          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={isStreaming || disabled || !value.trim()}
            title="Send message"
            aria-label="Send message"
          >
            {isStreaming ? (
              <div className={styles.spinner} />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {IMPROVE_PROMPT_ENABLED && (
            <button
              type="button"
              className={styles.improveBtn}
              onClick={handleImprove}
              disabled={!canImprove}
              title="Improve prompt (Cmd/Ctrl+I)"
              aria-label="Improve prompt"
            >
              {isImproving ? (
                <div className={styles.improveSpinner} />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M5 3l1.5 3.5L10 8l-3.5 1.5L5 13l-1.5-3.5L0 8l3.5-1.5L5 3zM18 9l1 2.5L21.5 12l-2.5 1L18 15l-1-2-2.5-1 2.5-1 1-2zM13 14l1 2.5L16.5 17l-2.5 1L13 20l-1-2-2.5-1 2.5-1 1-2z"
                    fill="currentColor"
                  />
                </svg>
              )}
              <span>Improve Prompt</span>
            </button>
          )}
          <div className={styles.hint}>
            {footerHint?.kind === 'revert' ? (
              <span className={styles.improvedHint}>
                Improved&nbsp;&middot;{' '}
                <button type="button" className={styles.revertLink} onClick={handleRevert}>
                  Revert
                </button>
              </span>
            ) : footerHint?.kind === 'no_change' ? (
              <span className={styles.improvedHint}>Already clear &mdash; nothing to improve.</span>
            ) : (
              'Enter to send, Shift+Enter for newline'
            )}
          </div>
        </div>
        <div className={styles.footerRight}>
          <AgentModeToggle disabled={isStreaming} />
          <CreditRing />
        </div>
      </div>
    </div>
  );
};
